/**
 * Button — primary, secondary, ghost, danger, warning, success variants.
 *
 * Replaces the ad-hoc inline-style buttons scattered across pages with
 * a single component that respects the theme variables and gives a
 * consistent shape, focus ring, and disabled state.
 *
 * Two axes:
 *   - `variant` = semantic color (primary/secondary/danger/warning/success, plus ghost).
 *   - `emphasis` = visual weight (`solid` fill · `soft` tinted · `outline`).
 *     Defaults preserve historical looks: primary/danger → solid,
 *     secondary → soft. All three treatments exist in the codebase today
 *     (e.g. a solid danger confirm, soft danger in the file browsers, and
 *     outline danger on the RBAC pages), so this axis is what lets those
 *     pages drop their hand-rolled `pillButton` helpers without visual drift.
 */

import { forwardRef, useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning' | 'success';
export type ButtonSize = 'sm' | 'md';
export type ButtonEmphasis = 'solid' | 'soft' | 'outline';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Visual weight. Defaults: primary/danger → `solid`, secondary → `soft`. Ignored for `ghost`. */
  emphasis?: ButtonEmphasis;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
  /** Fully-rounded (radius 999) shape instead of the default rounded-rect. */
  pill?: boolean;
}

const sizes: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '6px 12px', fontSize: 12, borderRadius: 7 },
  md: { padding: '9px 16px', fontSize: 13, borderRadius: 8 },
};

// Semantic color token per variant (used by soft/outline treatments).
const VARIANT_COLOR: Record<Exclude<ButtonVariant, 'ghost'>, string> = {
  primary: 'var(--accent)',
  secondary: 'var(--text-secondary)',
  danger: 'var(--red)',
  warning: 'var(--yellow)',
  success: 'var(--green)',
};

const VARIANT_GLOW: Record<Exclude<ButtonVariant, 'ghost'>, string> = {
  primary: 'var(--accent-glow)',
  secondary: 'var(--accent-glow)',
  danger: 'var(--red-glow)',
  warning: 'var(--yellow-glow)',
  success: 'var(--green-glow)',
};

function defaultEmphasis(v: ButtonVariant): ButtonEmphasis {
  return v === 'secondary' ? 'soft' : 'solid';
}

function variantStyle(v: ButtonVariant, e: ButtonEmphasis, disabled: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    fontWeight: 600,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'background 0.15s ease, transform 0.06s ease, box-shadow 0.15s ease, opacity 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  };

  if (v === 'ghost') {
    return { ...base, background: 'transparent', color: 'var(--text-secondary)' };
  }

  const color = VARIANT_COLOR[v];

  if (e === 'outline') {
    return {
      ...base,
      background: 'transparent',
      // Secondary outline reads as a neutral button → primary text; colored variants tint their text.
      color: v === 'secondary' ? 'var(--text-primary)' : color,
      borderColor: v === 'secondary' ? 'var(--border)' : color,
    };
  }

  if (e === 'soft') {
    return {
      ...base,
      background: 'var(--bg-tertiary)',
      color: v === 'secondary' ? 'var(--text-secondary)' : color,
      borderColor: 'var(--border)',
    };
  }

  // solid
  return {
    ...base,
    background: color,
    color: 'white',
    boxShadow: '0 1px 2px rgba(15,23,42,0.08), inset 0 -1px 0 rgba(0,0,0,0.06)',
  };
}

// Hover deltas, merged over the variant style while hovered (and enabled).
// Mirrors the theme.css .btn-accent / .btn-secondary :hover behavior that the
// old class-based buttons had — inline styles can't express :hover, so we drive
// it from a hovered state instead.
function hoverStyle(v: ButtonVariant, e: ButtonEmphasis): React.CSSProperties {
  if (v === 'ghost') {
    return { background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
  }
  if (e === 'solid') {
    return { opacity: 0.9 };
  }
  // soft / outline: lift toward the variant's glow + colored border/text.
  const color = VARIANT_COLOR[v];
  return {
    background: VARIANT_GLOW[v],
    borderColor: color,
    color,
  };
}

// Shared visual style for both <button> (Button) and <a> (ButtonLink) so the two
// stay pixel-identical. Excludes the caller's `style` override, which is merged last.
function composeStyle(
  variant: ButtonVariant,
  size: ButtonSize,
  eff: ButtonEmphasis,
  pill: boolean | undefined,
  isDisabled: boolean,
  hovered: boolean,
): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ...sizes[size],
    ...variantStyle(variant, eff, isDisabled),
    ...(hovered && !isDisabled ? hoverStyle(variant, eff) : null),
    ...(pill ? { borderRadius: 999 } : null),
  };
}


export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    emphasis,
    leftIcon,
    rightIcon,
    loading,
    pill,
    disabled,
    style,
    children,
    onMouseEnter,
    onMouseLeave,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const eff = emphasis ?? defaultEmphasis(variant);
  const [hovered, setHovered] = useState(false);
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      style={{
        ...composeStyle(variant, size, eff, pill, !!isDisabled, hovered),
        ...style,
      }}
      onMouseEnter={(e) => {
        setHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
      }}
      {...rest}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Visual weight. Defaults: primary/danger → `solid`, secondary → `soft`. */
  emphasis?: ButtonEmphasis;
  /** Fully-rounded (radius 999) shape instead of the default rounded-rect. */
  pill?: boolean;
}

/**
 * ButtonLink — an `<a>` styled exactly like `Button`. Use for real navigation /
 * download links that must remain anchors (e.g. a file download `href`) but read
 * as a button. Shares Button's variant/size/emphasis/pill visual axes.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'secondary', size = 'md', emphasis, pill, style, children, onMouseEnter, onMouseLeave, ...rest },
  ref,
) {
  const eff = emphasis ?? defaultEmphasis(variant);
  const [hovered, setHovered] = useState(false);
  return (
    <a
      ref={ref}
      style={{
        ...composeStyle(variant, size, eff, pill, false, hovered),
        ...style,
      }}
      onMouseEnter={(e) => {
        setHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
      }}
      {...rest}
    >
      {children}
    </a>
  );
});

function Spinner() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      style={{ animation: 'compass-spin 0.8s linear infinite' }}
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      <style>{`@keyframes compass-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}
